import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard } from '../ui/GlassComponents';
import { FiActivity, FiDatabase, FiCpu, FiUsers, FiTrendingUp, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';

const SystemHealthMonitor = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchHealthData = async () => {
    try {
      const response = await axios.get('/api/admin/system-health');
      if (response.data.success) {
        setHealthData(response.data.data);
        setLastUpdated(new Date());
      } else {
        toast.error('Failed to fetch system health data');
      }
    } catch (error) {
      console.error('Error fetching system health:', error);
      toast.error('Error fetching system health data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealthData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getHealthStatus = (errorRate, responseTime) => {
    if (errorRate > 5 || responseTime > 1000) return { status: 'critical', color: 'text-red-500', bg: 'bg-red-500/20' };
    if (errorRate > 2 || responseTime > 500) return { status: 'warning', color: 'text-yellow-500', bg: 'bg-yellow-500/20' };
    return { status: 'healthy', color: 'text-green-500', bg: 'bg-green-500/20' };
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 MB';
    const mb = bytes;
    return `${mb} MB`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            System Health Monitor
          </h2>
        </div>
        <GlassCard className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading system metrics...</p>
        </GlassCard>
      </div>
    );
  }

  if (!healthData) {
    return (
      <GlassCard className="p-8 text-center">
        <FiAlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Unable to Load System Health
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Failed to fetch system health metrics
        </p>
        <button
          onClick={fetchHealthData}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </GlassCard>
    );
  }

  const health = getHealthStatus(healthData.requests.errorRate, healthData.requests.avgResponseTime);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          System Health Monitor
        </h2>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Last updated: {lastUpdated?.toLocaleTimeString()}
          </span>
          <button
            onClick={fetchHealthData}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-500 transition-colors"
            title="Refresh metrics"
          >
            <FiRefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Overall System Status */}
      <GlassCard className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              System Status
            </h3>
            <div className="flex items-center space-x-3">
              <div className={`h-3 w-3 rounded-full ${health.status === 'healthy' ? 'bg-green-500' : health.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'} animate-pulse`}></div>
              <span className={`font-medium ${health.color}`}>
                {health.status === 'healthy' ? 'All Systems Operational' : 
                 health.status === 'warning' ? 'Performance Warning' : 'Critical Issues Detected'}
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600 dark:text-gray-400">Uptime</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {healthData.uptime.formatted}
            </p>
          </div>
        </div>
      </GlassCard>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* API Performance */}
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <FiActivity className="h-8 w-8 text-blue-500" />
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${health.bg} ${health.color}`}>
              {health.status}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">API Performance</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Avg Response</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {healthData.requests.avgResponseTime}ms
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Error Rate</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {healthData.requests.errorRate}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Total Requests</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {healthData.requests.total.toLocaleString()}
              </span>
            </div>
          </div>
        </GlassCard>

        {/* Database Health */}
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <FiDatabase className="h-8 w-8 text-green-500" />
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-500">
              {healthData.database.connectionStatus}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Database Health</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Response Time</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {healthData.database.responseTime}ms
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Total Records</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {Object.values(healthData.database.tables).reduce((a, b) => a + b, 0).toLocaleString()}
              </span>
            </div>
          </div>
        </GlassCard>

        {/* System Resources */}
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <FiCpu className="h-8 w-8 text-purple-500" />
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-500">
              {healthData.system.cores} cores
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">System Resources</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Memory Used</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatBytes(healthData.system.memory.used)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Memory Total</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatBytes(healthData.system.memory.total)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Node Version</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {healthData.system.nodeVersion}
              </span>
            </div>
          </div>
        </GlassCard>

        {/* User Activity */}
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <FiUsers className="h-8 w-8 text-indigo-500" />
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-500/20 text-indigo-500">
              {healthData.userActivity.activityRate}% active
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">User Activity</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Total Users</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {healthData.userActivity.totalUsers.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Active (24h)</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {healthData.userActivity.activeUsers24h.toLocaleString()}
              </span>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top API Endpoints */}
        <GlassCard className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FiTrendingUp className="h-5 w-5 mr-2 text-blue-500" />
            Top API Endpoints
          </h3>
          <div className="space-y-3">
            {healthData.endpoints.slice(0, 5).map((endpoint, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white text-sm">
                    {endpoint.endpoint}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {endpoint.count} requests • {endpoint.avgTime}ms avg
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-medium ${endpoint.errorRate > 5 ? 'text-red-500' : endpoint.errorRate > 2 ? 'text-yellow-500' : 'text-green-500'}`}>
                    {endpoint.errorRate}% errors
                  </span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Feature Usage */}
        <GlassCard className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FiActivity className="h-5 w-5 mr-2 text-green-500" />
            Feature Usage (7 days)
          </h3>
          <div className="space-y-3">
            {healthData.featureUsage.slice(0, 5).map((feature, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white text-sm">
                    {feature.feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-blue-500">
                    {feature.usage.toLocaleString()} uses
                  </span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Team Health & Error Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Health */}
        <GlassCard className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FiUsers className="h-5 w-5 mr-2 text-purple-500" />
            Team Health Status
          </h3>
          <div className="space-y-3">
            {Object.entries(healthData.teamHealth).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between p-3 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`h-3 w-3 rounded-full ${
                    status === 'active' ? 'bg-green-500' : 
                    status === 'trialing' ? 'bg-blue-500' : 
                    status === 'past_due' ? 'bg-yellow-500' : 'bg-red-500'
                  }`}></div>
                  <span className="font-medium text-gray-900 dark:text-white text-sm">
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {count} teams
                </span>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Error Metrics */}
        <GlassCard className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FiAlertTriangle className="h-5 w-5 mr-2 text-red-500" />
            Error Metrics
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-red-50/50 dark:bg-red-900/20 rounded-lg border border-red-200/50 dark:border-red-800/50">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-red-700 dark:text-red-400">API Errors (24h)</span>
                <span className="text-lg font-bold text-red-600 dark:text-red-400">
                  {healthData.errorMetrics.recentErrors24h}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-red-700 dark:text-red-400">Error Rate</span>
                <span className="text-lg font-bold text-red-600 dark:text-red-400">
                  {healthData.errorMetrics.apiErrorRate}%
                </span>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50/50 dark:bg-blue-900/20 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Total API Requests</span>
                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  {healthData.requests.total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Database Table Statistics */}
      <GlassCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <FiDatabase className="h-5 w-5 mr-2 text-green-500" />
          Database Statistics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(healthData.database.tables).map(([table, count]) => (
            <div key={table} className="text-center p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {count.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                {table}
              </p>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
};

export default SystemHealthMonitor;
